import { useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import { FieldError } from '@/shared/components/ui/field-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  getBackendFieldErrors,
  parseZod,
  scrollToFirstError,
} from '@/shared/lib/formValidation'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

import { usePurchase } from '../../hooks/usePurchase'
import { personFormSchema } from '../../schemas/person.schema'

export function PurchaseForm({ purchase, onCancel, onSuccess }) {
  const submitLockRef = useRef(false)

  const [formData, setFormData] = useState(() => ({
    name: purchase?.name || '',
    email: purchase?.email || '',
    phoneNumber: purchase?.phoneNumber || '',
  }))

  const [errors, setErrors] = useState({})

  const { createPurchasePerson, updatePurchasePerson, isSubmitting } =
    usePurchase()

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev

      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    clearError(name)

    let sanitized = value
    if (name === 'name') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
    } else if (name === 'phoneNumber') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitized,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isSubmitting || submitLockRef.current) return

    submitLockRef.current = true

    const parsed = parseZod(personFormSchema, formData)

    if (!parsed.success) {
      submitLockRef.current = false
      setErrors(parsed.errors)
      toast.error(parsed.message)
      scrollToFirstError()
      return
    }

    try {
      setErrors({})
      if (purchase) {
        await updatePurchasePerson(purchase._id || purchase.id, parsed.data)
      } else {
        await createPurchasePerson(parsed.data)
      }
      onSuccess?.()
    } catch (error) {
      setErrors(getBackendFieldErrors(error))
    } finally {
      submitLockRef.current = false
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="purchase-name">FULL NAME</Label>
          <Input
            id="purchase-name"
            name="name"
            placeholder="Enter full name"
            value={formData.name}
            onChange={handleChange}
            aria-invalid={Boolean(errors.name)}
            data-invalid={errors.name ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.name && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="purchase-email">EMAIL</Label>
          <Input
            id="purchase-email"
            name="email"
            type="email"
            placeholder="Enter email"
            value={formData.email}
            onChange={handleChange}
            aria-invalid={Boolean(errors.email)}
            data-invalid={errors.email ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.email && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.email} />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="purchase-phoneNumber">PHONE NUMBER</Label>
          <Input
            id="purchase-phoneNumber"
            name="phoneNumber"
            maxLength={10}
            placeholder="Enter 10-digit phone number"
            value={formData.phoneNumber}
            onChange={handleChange}
            aria-invalid={Boolean(errors.phoneNumber)}
            data-invalid={errors.phoneNumber ? 'true' : undefined}
            className={cn(
              'h-12 rounded-xl',
              errors.phoneNumber && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
          <FieldError message={errors.phoneNumber} />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 sm:gap-4 border-t pt-6">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onCancel}
          disabled={isSubmitting}
          className="w-full sm:w-auto h-12 sm:min-w-28"
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="ghost"
          size="lg"
          disabled={isSubmitting}
          className="w-full sm:w-auto h-12 sm:min-w-40 gap-2 bg-[#0F172A] hover:bg-gray-900 text-white hover:text-white"
        >
          {isSubmitting
            ? purchase
              ? 'Updating…'
              : 'Creating…'
            : purchase
              ? 'Update Purchase'
              : 'Create Purchase'}
        </Button>
      </div>
    </form>
  )
}
